/**
 * Main game store using Zustand with Immer
 * Combines GameState (persisted) and UIState (ephemeral)
 */

import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import {
    AnimationStep,
    GameActions,
    GameOverReason,
    GameState,
    HistoryEntry,
    OverlayType,
    RoomVariant,
    Stats,
    ToastMessage,
    ToastStatChange,
    UIActions,
    UIState,
} from "@/types/state";
import {
    createInitialGameState,
    createInitialUIState,
    INITIAL_GAME_STATE,
    INITIAL_UI_STATE,
} from "@/data/initialState";
import type { Message as GqlMessage, MessageResponse } from "@/types/api";
import { Enum_Message_Type } from "@/utils/graphql/generated/graphql";
import { MAX_HEARTS } from "@/config/gameConfig";
import { playerIsBankrupt, playerIsUnhappy } from "@/utils/gameOverUtils";
import { useCacheStore } from "@/state/cacheStore";
import { pickRoomVariant } from "@/utils/roomUtils";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function createHistoryEntry(type: HistoryEntry["type"], change: number, newValue: number, title: string): HistoryEntry {
    return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        change,
        newValue,
        title,
        type,
        timestamp: Date.now(),
    };
}

/**
 * Build the animation step queue from a response and its parent message.
 */
function buildAnimationSteps(response: MessageResponse, message: GqlMessage): AnimationStep[] {
    const steps: AnimationStep[] = [];
    const toasts: ToastMessage[] = [];

    if (response.cash && response.cash !== 0) {
        toasts.push({ type: "cash", change: response.cash });
    }
    if (response.investments && response.investments !== 0) {
        toasts.push({ type: "depot", change: response.investments });
    }

    if (response.earnings && response.earnings !== 0) {
        toasts.push({ type: "earnings", change: response.earnings });
    }

    if (response.happiness && response.happiness !== 0) {
        toasts.push({ type: "happiness", change: response.happiness });
    }
    if (response.achievement) {
        toasts.push({ type: "achievement", achievement: response.achievement });
    }
    if (response.item) {
        toasts.push({ type: "item", item: response.item });
    }

    if (toasts.length > 0) {
        steps.push({ type: "toasts", toasts });
    }

    if (response.item?.documentId) {
        steps.push({ type: "item-appear", item: response.item });
    }

    if (message.educational_message) {
        steps.push({ type: "educational", educationalMessage: message.educational_message });
    }

    return steps;
}

/**
 * Check if applying the response's stat changes would trigger game over.
 * Game over when: cash would go negative OR happiness would reach zero.
 * Bankruptcy takes precedence over unhappiness if both would occur simultaneously.
 */
function checkForGameOver(stats: Stats, response: MessageResponse): GameOverReason {
    const newCash = stats.cash + (response.cash ?? 0);
    const newHappiness = stats.happiness + (response.happiness ?? 0);

    if (playerIsBankrupt(newCash)) return "cash";
    if (playerIsUnhappy(newHappiness)) return "happiness";

    return "none";
}

/**
 * When a PreRoomTransition message becomes current, resolve which room
 * the player will move into next and store it so both display-text and
 * game-over logic can read it from one place.
 */
function resolveNextRoom(state: GameStore): void {
    const message = state.currentMessage;

    if (!message || message.type !== Enum_Message_Type.PreRoomTransition) return;

    const phases = useCacheStore.getState().phases;
    const currentPhaseIndex = phases.findIndex((p) => p.documentId === state.currentPhaseId);
    const nextPhase = phases[currentPhaseIndex + 1] ?? null;
    if (!nextPhase) return;

    const variant = pickRoomVariant(
        nextPhase.room_a ?? null,
        nextPhase.room_b ?? null,
        state.stats.cash,
        state.stats.investments,
    );
    const room = variant === "a" ? nextPhase.room_a : nextPhase.room_b;
    state.nextRoom = room ?? null;
    state.nextRoomVariant = variant;
}

/**
 * Advance the message queue and start the queue timer.
 * Shared logic used by both processResponse (no animations) and
 * advanceAnimationStep (after animations complete).
 */
function advanceQueueAndStartTimer(state: GameStore): void {
    const followUp = state.pendingFollowUpMessage;
    const gameOverReason = state.gameOverReason;

    if (gameOverReason !== "none") {
        return; // Don't advance queue if game is over, just clear message
    }
    if (followUp) {
        state.currentMessage = followUp;
        state.pendingFollowUpMessage = null;
        state.completedPhaseMessages += 1;
        state.effectivePhaseMessageTotal += 1;
    } else if (state.messageQueue.length > 0) {
        state.currentMessage = state.messageQueue[0];
        state.messageQueue = state.messageQueue.slice(1);
        state.completedPhaseMessages += 1;
    } else {
        state.currentMessage = null;
        return; // No next message — skip the timer entirely
    }

    resolveNextRoom(state);

    state.queueTimerActive = true;
    state.queueTimerElapsed = 0;
}

// -----------------------------------------------------------------------------
// Combined Store Type
// -----------------------------------------------------------------------------

type GameStore = GameState & UIState & GameActions & UIActions;

// -----------------------------------------------------------------------------
// Store Creation
// -----------------------------------------------------------------------------

export const useGameStore = create<GameStore>()(
    immer((set, get) => {
        return {
            // ----- Persisted Game State -----
            ...INITIAL_GAME_STATE,

            // ----- Ephemeral UI State -----
            ...INITIAL_UI_STATE,

            // =================================================================
            // Game Actions
            // =================================================================

            setCurrentPhaseId: (phaseId: string | null) => {
                set((state) => {
                    state.currentPhaseId = phaseId;
                });
            },

            setFinanceTypeId: (financeTypeId: string | null) => {
                set((state) => {
                    if (state.financeTypeId == null) {
                        state.financeTypeId = financeTypeId;
                    }
                });
            },

            startPhase: (phaseId: string, phaseMessages: GqlMessage[]) => {
                set((state) => {
                    state.currentPhaseId = phaseId;
                    state.currentRoomVariant = null;
                    state.completedPhaseMessages = 0;
                    state.currentMessage = null;
                    state.messageQueue = phaseMessages;
                    state.effectivePhaseMessageTotal = phaseMessages.length;
                });
            },

            completePhase: () => {
                set((state) => {
                    if (state.currentPhaseId && !state.completedPhaseIds.includes(state.currentPhaseId)) {
                        state.completedPhaseIds.push(state.currentPhaseId);
                    }
                });
            },

            selectRoom: (variant: RoomVariant) => {
                set((state) => {
                    state.currentRoomVariant = variant;
                    state.completedPhaseMessages = 0;
                });
            },

            /**
             * Kick off the queue timer so the first message appears after the
             * standard delay. Called by PlayingScreen when phaseReady becomes true.
             */
            startMessageQueue: () => {
                set((state) => {
                    if (state.messageQueue.length > 0) {
                        state.currentMessage = state.messageQueue[0];
                        state.messageQueue = state.messageQueue.slice(1);
                        state.queueTimerActive = true;
                        state.queueTimerElapsed = 0;
                    }
                });
            },

            // ----- Granular Stat Mutations -----
            addCash: (amount: number, historyEntryTitle: string) => {
                set((state) => {
                    state.stats.cash += amount;
                    if (amount !== 0) {
                        state.history.push(createHistoryEntry("cash", amount, state.stats.cash, historyEntryTitle));
                    }
                });
            },

            addInvestments: (amount: number, historyEntryTitle: string) => {
                set((state) => {
                    state.stats.investments += amount;
                    if (amount !== 0) {
                        state.history.push(
                            createHistoryEntry("depot", amount, state.stats.investments, historyEntryTitle),
                        );
                    }
                });
            },

            addHappiness: (amount: number) => {
                set((state) => {
                    const currentCount = state.stats.happiness;
                    if ((amount > 0 && currentCount < MAX_HEARTS) || (amount < 0 && currentCount > 0)) {
                        state.stats.happiness += amount;
                    }
                });
            },

            addItem: (itemId: string) => {
                set((state) => {
                    if (!state.inventory.includes(itemId)) {
                        state.inventory.push(itemId);
                    }
                });
            },

            unlockAchievement: (achievementId: string) => {
                set((state) => {
                    if (!state.unlockedAchievements.includes(achievementId)) {
                        state.unlockedAchievements.push(achievementId);
                    }
                });
            },

            clearNextRoom: () => {
                set((state) => {
                    state.nextRoom = null;
                    state.nextRoomVariant = null;
                });
            },

            resetGame: () => {
                set((state) => {
                    const fresh = createInitialGameState();
                    const freshUI = createInitialUIState();

                    // Reset game state
                    state.version = fresh.version;
                    state.gameIsFinished = fresh.gameIsFinished;
                    state.gameOverReason = fresh.gameOverReason;
                    state.currentPhaseId = fresh.currentPhaseId;
                    state.currentRoomVariant = fresh.currentRoomVariant;
                    state.completedPhaseMessages = fresh.completedPhaseMessages;
                    state.stats = fresh.stats;
                    state.inventory = fresh.inventory;
                    state.unlockedAchievements = fresh.unlockedAchievements;
                    state.completedPhaseIds = fresh.completedPhaseIds;
                    state.financeTypeId = fresh.financeTypeId;

                    // Reset UI state
                    state.isPaused = freshUI.isPaused;
                    state.activeOverlay = freshUI.activeOverlay;
                    state.history = freshUI.history;
                    state.pendingResponse = freshUI.pendingResponse;
                    state.effectivePhaseMessageTotal = freshUI.effectivePhaseMessageTotal;
                    state.revealedItemId = freshUI.revealedItemId;

                    // Reset queue timer state
                    state.queueTimerActive = freshUI.queueTimerActive;
                    state.queueTimerElapsed = freshUI.queueTimerElapsed;
                    state.queueTimerDuration = freshUI.queueTimerDuration;

                    // Reset message queue state
                    state.currentMessage = freshUI.currentMessage;
                    state.messageQueue = freshUI.messageQueue;

                    // next room details are resolved dynamically before the room transition
                    state.nextRoom = null;
                    state.nextRoomVariant = null;

                    // Reset animation sequencer state
                    state.postMessageAnimationQueue = freshUI.postMessageAnimationQueue;
                    state.currentPostMessageAnimationStep = freshUI.currentPostMessageAnimationStep;
                    state.pendingFollowUpMessage = freshUI.pendingFollowUpMessage;
                    state.darkOverlayVisible = freshUI.darkOverlayVisible;
                });
            },
            // for debugging purposes only, not used in actual game flow
            setHappiness: (amount: number) => {
                set((state) => {
                    state.stats.happiness = amount;
                });
            },

            // =================================================================
            // UI Actions
            // =================================================================
            setOverlay: (overlay: OverlayType) => {
                set((state) => {
                    state.activeOverlay = overlay;
                    state.isPaused = overlay !== OverlayType.None;
                });
            },
            setRevealedItemId: (id: string) => set({ revealedItemId: id }),
            clearRevealedItemId: () => set({ revealedItemId: null }),

            // ----- Response Flow -----
            setPendingResponse: (response: MessageResponse) => {
                set((state) => {
                    state.pendingResponse = response;
                });
            },

            // Called by the message component when the response-click exit animation completes. Handles the entire flow of:
            // - Determining game-over reason (if any)
            // - Building the post-message animation queue
            // - Starting the animation sequence or advancing the message queue immediately if no animations
            // - Stat changes are being applied during the animation sequence when the respective toast step was played
            processResponse: () => {
                const { pendingResponse, currentMessage, stats } = get();
                if (!pendingResponse || !currentMessage) return;

                // --- Determine game-over reason ---
                let gameOverReason: GameOverReason = "none";

                if (pendingResponse.doomspendingGameOver === true) {
                    gameOverReason = "cash";
                } else {
                    gameOverReason = checkForGameOver(stats, pendingResponse);
                }

                // Rent game-over: if the current message is a PreRoomTransition
                // and the player can't afford the next room's rent, end the game
                // instead of transitioning into a phase they'd immediately lose in.
                if (gameOverReason === "none") {
                    const { nextRoom } = get();
                    if (
                        currentMessage.type === Enum_Message_Type.PreRoomTransition &&
                        nextRoom?.rent &&
                        nextRoom.rent > 0 &&
                        nextRoom.rent > stats.cash + (pendingResponse.cash ?? 0)
                    ) {
                        gameOverReason = "rent";
                    }
                }
                // Doomspending: override cash to drain entire balance in following toast message ---
                const response = { ...pendingResponse };
                if (response.doomspendingGameOver === true) {
                    response.cash = -stats.cash;
                }

                // --- Build animation steps (always — so the player sees consequences) ---
                let steps = buildAnimationSteps(response, currentMessage);

                // Strip achievement toasts when the player is about to game over —
                // no point unlocking achievements they won't be able to see.
                if (gameOverReason !== "none") {
                    const filteredSteps: AnimationStep[] = [];
                    for (const step of steps) {
                        if (step.type === "item-appear") continue;
                        if (step.type !== "toasts") {
                            filteredSteps.push(step);
                            continue;
                        }
                        const filtered = step.toasts.filter(
                            (t): t is ToastStatChange => t.type !== "achievement" && t.type !== "item",
                        );
                        if (filtered.length > 0) {
                            filteredSteps.push({ type: "toasts", toasts: filtered });
                        }
                    }
                    steps = filteredSteps;
                }

                const followUp = pendingResponse.followUpMessage ?? null;

                set((state) => {
                    state.pendingResponse = null;
                    state.gameOverReason = gameOverReason;

                    // Finance type (write-once)
                    if (state.financeTypeId === null && !!pendingResponse.financeType?.documentId) {
                        state.financeTypeId = pendingResponse.financeType.documentId;
                    }

                    if (steps.length > 0) {
                        // Has animations — play them first, then handle game-over or queue advance
                        state.postMessageAnimationQueue = steps.slice(1);
                        state.currentPostMessageAnimationStep = steps[0] ?? null;
                        state.pendingFollowUpMessage = followUp ?? null;
                        const first = steps[0];
                        state.darkOverlayVisible = first?.type === "item-appear" || first?.type === "educational";
                    } else if (gameOverReason !== "none") {
                        // No animations and game over — end immediately
                        state.gameIsFinished = true;
                        state.currentMessage = null;
                        state.queueTimerActive = false;
                    } else {
                        // No animations, no game over — advance queue and start timer
                        state.pendingFollowUpMessage = followUp;
                        advanceQueueAndStartTimer(state as unknown as GameStore);
                    }
                });
            },

            finishGame: () => {
                set((state) => {
                    state.gameIsFinished = true;
                    state.queueTimerActive = false;
                    state.currentMessage = null;
                });
            },

            // ----- Queue Timer Actions -----
            updateQueueTimerElapsed: (elapsed: number) => {
                set((state) => {
                    state.queueTimerElapsed = elapsed;
                });
            },

            endQueueTimer: () => {
                set((state) => {
                    state.queueTimerActive = false;
                });
            },

            setQueueTimerDuration: (durationMs: number) => {
                set((state) => {
                    state.queueTimerDuration = durationMs;
                });
            },

            // ----- Message Consequences Animation Sequencer Actions -----
            advancePostMessageAnimationStep: () => {
                const { postMessageAnimationQueue, gameOverReason } = get();

                if (postMessageAnimationQueue.length > 0) {
                    // More steps to play
                    set((state) => {
                        const next = state.postMessageAnimationQueue.shift()!;
                        state.currentPostMessageAnimationStep = next;
                        state.darkOverlayVisible = next.type === "item-appear" || next.type === "educational";
                    });
                } else {
                    // Sequence complete
                    set((state) => {
                        state.currentPostMessageAnimationStep = null;
                        state.postMessageAnimationQueue = [];
                        state.darkOverlayVisible = false;
                        if (gameOverReason !== "none") {
                            // Game over — let PlayingScreen pick this up via gameIsFinished
                            state.gameIsFinished = true;
                            state.currentMessage = null;
                            state.queueTimerActive = false;
                            state.pendingFollowUpMessage = null;
                        } else {
                            advanceQueueAndStartTimer(state as unknown as GameStore);
                        }
                    });
                }
            },
        };
    }),
);
